/**
 * @typedef GeocodingErrors
 * @property {string} zeroResults - Indica que la geolocalización se realizó
 * correctamente, pero no mostró resultados. Esto puede ocurrir si se pasó un
 * valor `address` inexistente al geocodificador
 */

/**
 * @type GeocodingErrors
 * @link [Documentación Google Geocoding API](https://developers.google.com/maps/documentation/geocoding/requests-geocoding?hl=es-419#StatusCodes)
 */
export const GEOCODING_ERRORS = {
  zeroResults: 'ZERO_RESULTS',
}

/**
 * @typedef ProvinceAndLocalities
 * @property {string} province
 * @property {string} city
 * @property {boolean} hasSeveralLocalities
 * @property {string[]} localitiesOptions
 */

/**
 * @returns {Promise<ProvinceAndLocalities>}
 */

const calculateProvinceAndLocalities = (postalCode, countryCode, fromSearch) =>
  new Promise((resolve, reject) => {
    const geocoder = new window.google.maps.Geocoder()
    geocoder
      .geocode({
        componentRestrictions: {
          country: countryCode,
          postalCode: postalCode,
        },
      })
      .then(({ results }) => {
        const result = results[0]

        const localitiesInfo = result.address_components.find(x =>
          x.types.includes('locality', 'political'),
        )

        const provinceInfo =
          result.address_components?.find(x =>
            x.types.includes(
              ['ES'].includes(countryCode)
                ? 'administrative_area_level_2'
                : 'administrative_area_level_1',
            ),
          ) ??
          result.address_components?.find(x =>
            x.types.includes(
              ['ES'].includes(countryCode)
                ? 'administrative_area_level_2'
                : 'administrative_area_level_1',
            ),
          ) ??
          localitiesInfo
        const postalCodeInfo = result.address_components.find(x =>
          x.types.includes('postal_code'),
        )
        const hasSeveralLocalities =
          result.postcode_localities !== undefined &&
          localitiesInfo === undefined
        const province = provinceInfo?.long_name ?? ''
        const localities = hasSeveralLocalities
          ? ''
          : localitiesInfo?.long_name ?? ''
        if (fromSearch) {
          const postalCodeInfo = fromSearch.address_components.find(x =>
            x.types.includes('postal_code'),
          )
          if (postalCode !== postalCodeInfo?.long_name) {
            resolve({
              province,
              hasSeveralLocalities,
              localitiesOptions: localitiesInfo
                ? [localitiesInfo?.long_name]
                : result.postcode_localities,
              city: localities,
              postalCode: postalCodeInfo?.long_name ?? postalCode,
            })
            return
          }
          const localitiesInfo = fromSearch.address_components.find(x =>
            x.types.includes('locality', 'political'),
          )
          const provinceInfo =
            fromSearch.address_components.find(x =>
              x.types.includes(
                ['ES'].includes(countryCode)
                  ? 'administrative_area_level_2'
                  : 'administrative_area_level_1',
              ),
            ) ??
            fromSearch.address_components.find(x =>
              x.types.includes(
                ['ES'].includes(countryCode)
                  ? 'administrative_area_level_2'
                  : 'administrative_area_level_1',
              ),
            ) ??
            localitiesInfo
          const hasSeveralLocalities =
            fromSearch.postcode_localities !== undefined
          const province = provinceInfo?.long_name ?? ''
          const localities = hasSeveralLocalities
            ? ''
            : localitiesInfo?.long_name ?? ''
          if (!localitiesInfo && province?.length) {
            const result = results[0]
            const localitiesInfo = result.address_components.find(x =>
              x.types.includes('locality', 'political'),
            )

            const provinceInfo =
              result.address_components?.find(x =>
                x.types.includes(
                  ['ES'].includes(countryCode)
                    ? 'administrative_area_level_2'
                    : 'administrative_area_level_1',
                ),
              ) ??
              result.address_components?.find(x =>
                x.types.includes(
                  ['ES'].includes(countryCode)
                    ? 'administrative_area_level_2'
                    : 'administrative_area_level_1',
                ),
              ) ??
              localitiesInfo
            const postalCodeInfo = result.address_components.find(x =>
              x.types.includes('postal_code'),
            )

            const hasSeveralLocalities =
              result.postcode_localities !== undefined &&
              localitiesInfo === undefined
            const province = provinceInfo?.long_name ?? ''
            const localities = hasSeveralLocalities
              ? ''
              : localitiesInfo?.long_name ?? ''
            resolve({
              province,
              hasSeveralLocalities,
              localitiesOptions: localitiesInfo
                ? [localitiesInfo?.long_name]
                : result.postcode_localities,
              city: localities,
              postalCode: postalCodeInfo?.long_name ?? postalCode,
            })
          } else
            resolve({
              province,
              hasSeveralLocalities,
              localitiesOptions: localitiesInfo
                ? [localitiesInfo?.long_name]
                : result.postcode_localities,
              city: localities,
            })
        } else if (localitiesInfo)
          resolve({
            province,
            hasSeveralLocalities,
            localitiesOptions: localitiesInfo
              ? [localitiesInfo?.long_name]
              : result.postcode_localities,
            city: localities,
            postalCode: postalCodeInfo?.long_name ?? postalCode,
          })
        else
          resolve({
            province,
            hasSeveralLocalities,
            localitiesOptions: localitiesInfo
              ? [localitiesInfo?.long_name]
              : result.postcode_localities,
            city: localities,
            postalCode: postalCodeInfo?.long_name ?? postalCode,
          })
      })
      .catch(error => {
        reject(error)
      })
  })

export default calculateProvinceAndLocalities
